import {
  backButton,
  hapticFeedback,
  init as initSdk,
  miniApp,
  retrieveRawInitData,
  themeParams,
  viewport,
} from '@telegram-apps/sdk-react'

/**
 * The Telegram surface, kept behind one module so the rest of the app does not
 * care whether it is running inside the messenger. Every call degrades to a
 * no-op in a plain browser, because `pnpm dev` outside Telegram has to work.
 */

let insideTelegram = false

export function isInsideTelegram(): boolean {
  return insideTelegram
}

/**
 * Everything the first paint needs, and nothing that can wait on the bridge.
 *
 * This function is synchronous **on purpose**. The first version awaited
 * `viewport.mount()` before rendering, and that call waits for a reply from
 * Telegram's bridge — outside a real Mini App the reply never comes, the promise
 * never settles, and the app renders nothing at all. The UI must not be able to
 * hang on the messenger.
 */
export function initTelegramSync(): void {
  try {
    initSdk()
    insideTelegram = true
  } catch {
    insideTelegram = false
  }

  if (!insideTelegram) {
    applyTheme(prefersDarkFromBrowser() ? 'dark' : 'light')
    return
  }

  try {
    // `mountSync`, not `mount` — the async variants are deprecated in SDK v3.
    if (miniApp.mountSync.isAvailable()) miniApp.mountSync()
    if (themeParams.mountSync.isAvailable()) themeParams.mountSync()
    if (backButton.isSupported()) backButton.mount()

    /*
     * We ship our own palette rather than inheriting Telegram's, but we read
     * `colorScheme` so the app is in the same mode as the messenger around it,
     * and we push our own background up to the header so the Mini App does not
     * look pasted in (CLAUDE.md > Design system).
     */
    applyTheme(miniApp.isDark() ? 'dark' : 'light')

    if (miniApp.setHeaderColor.isAvailable()) miniApp.setHeaderColor(readToken('--bg'))
    if (miniApp.setBackgroundColor.isAvailable()) miniApp.setBackgroundColor(readToken('--bg'))
  } catch (err) {
    // A cosmetic bridge call is never worth a blank screen.
    console.warn('Telegram surface partially unavailable', err)
    applyTheme(prefersDarkFromBrowser() ? 'dark' : 'light')
  }
}

/**
 * The parts that legitimately need a round trip. Called after the first render
 * and deliberately never awaited by it.
 */
export function initViewport(): void {
  if (!insideTelegram || !viewport.mount.isAvailable()) return

  viewport
    .mount()
    .then(() => {
      viewport.expand()
    })
    .catch((err: unknown) => {
      console.warn('viewport.mount failed', err)
    })
}

function prefersDarkFromBrowser(): boolean {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false
}

function readToken(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim()
}

export function applyTheme(theme: 'light' | 'dark'): void {
  document.documentElement.dataset.theme = theme
}

/**
 * Haptics do more for the feel than any animation and cost nothing, so they fire
 * on every rating and every answer (CLAUDE.md > Design system).
 */
export const haptics = {
  tap(): void {
    if (hapticFeedback.impactOccurred.isAvailable()) hapticFeedback.impactOccurred('light')
  },
  success(): void {
    if (hapticFeedback.notificationOccurred.isAvailable()) {
      hapticFeedback.notificationOccurred('success')
    }
  },
  error(): void {
    if (hapticFeedback.notificationOccurred.isAvailable()) {
      hapticFeedback.notificationOccurred('error')
    }
  },
}

/** The raw `initData` string, passed to the API verbatim for HMAC validation. */
export function getInitDataRaw(): string | null {
  // The *raw* string, not the parsed object: re-serialising it would reorder or
  // re-encode fields and the server's HMAC would no longer match.
  try {
    return retrieveRawInitData() ?? null
  } catch {
    return null
  }
}
